import { Icon, IconsName, Loading } from '@/components';
import Chip, { ChipIntent } from '@/components/atoms/Chip/Chip';
import APP_CONFIG from '@/config/app';
import { IconVariants } from '@/interfaces';
import { cva, VariantProps } from 'class-variance-authority';
import {
  ButtonHTMLAttributes,
  forwardRef,
  isValidElement,
  ReactNode,
  useState,
} from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const mainGap = APP_CONFIG.STYLES.MAIN_GAP;
const wholeStepDecrement = APP_CONFIG.STYLES.WHOLESTEP_DECREMENT;
const mainRoundedness = APP_CONFIG.STYLES.MAIN_ROUNDEDNESS;
const spacingConfig = {
  paddingWhenButtonIsGhostIntent: 'p-0',
  paddingWhenButtonIsNotGhostIntent: `p${mainGap}`,
  gap: `gap${mainGap}${wholeStepDecrement}`,
  paddingWhenButtonIsChipIntent: 'px-sm py-2xs',
};
const roundednessConfig = {
  roundedness: `rounded${mainRoundedness}`,
  iconRoundedness: 'rounded-none',
  chipRoundedness: `rounded-full`,
};
const sizingConfig = {
  widthWhenButtonIsFullWidth: 'w-full',
  innerWidth: 'w-full',
  height: 'h-auto',
};
const buttonColorClasses = {
  primary: {
    background: 'bg-accent/10',
    border: 'border border-accent/10',
    text: 'text-primary/80',
    hover: {
      background: 'hover:bg-accent/15',
      border: 'hover:border-accent/15',
      text: 'hover:text-primary',
    },
    active: {
      background: 'active:bg-accent/20',
      border: 'active:border-accent/20',
    },
    disabled: {
      background: 'disabled:bg-accent/5',
      border: 'disabled:border border-accent/5',
      text: 'disabled:text-primary/20',
      hover: {
        background: 'disabled:hover:bg-accent/5',
        border: 'disabled:hover:border disabled:hover:border-accent/5',
        text: 'disabled:hover:text-primary/20',
      },
    },
  },
  ghost: {
    background: 'bg-transparent bg-opacity-0',
    border: 'border-none',
    text: 'text-accent/90',
    hover: {
      text: 'hover:text-accent',
    },
    active: {
      text: 'active:text-accent/50',
    },
    disabled: {
      text: 'disabled:text-primary/20',
      hover: {
        text: 'disabled:hover:text-primary/20',
      },
    },
  },
  secondary: {
    background: 'bg-accent/5',
    border: 'border border-accent/10',
    text: 'cursor-pointer',
    hover: {
      background: 'hover:bg-accent/10',
      border: 'hover:border-accent/15',
    },
    active: {
      background: 'active:bg-accent/15',
      border: 'active:border-accent/20',
    },
    disabled: {
      background: 'disabled:bg-accent/5',
      border: 'disabled:border border-accent/5',
      text: 'disabled:text-primary/20',
      hover: {
        background: 'disabled:hover:bg-accent/5',
        border: 'disabled:hover:border disabled:hover:border-accent/5',
        text: 'disabled:hover:text-primary/20',
      },
    },
  },
  ai: {
    background: 'bg-ai/15',
    border: 'border border-ai/20',
    text: 'text-ai cursor-pointer',
    hover: {
      background: 'hover:bg-ai/20',
      border: 'hover:border-ai/25',
      text: 'hover:text-ai',
    },
    active: {
      background: 'active:bg-ai/25',
      border: 'active:border-ai/30',
      text: 'active:text-ai/80',
    },
    disabled: {
      background: 'disabled:bg-ai/5',
      border: 'disabled:border border-ai/5',
      text: 'disabled:text-primary/20',
      hover: {
        background: 'disabled:hover:bg-ai/5',
        border: 'disabled:hover:border disabled:hover:border-ai/5',
        text: 'disabled:hover:text-primary/20',
      },
    },
  },
  chip: {
    background: 'bg-accent/5',
    border: 'border-accent/5',
    text: 'text-secondary',
    hover: {
      background: 'hover:bg-accent/30',
      border: 'hover:border-accent/30',
      text: 'hover:text-primary',
    },
    active: {
      background: 'bg-accent/30',
      border: 'border-accent/30',
      text: 'text-primary',
    },
    disabled: {
      background: 'disabled:bg-transparent',
      border: 'disabled:border border-accent/5',
      text: 'disabled:text-primary/20',
      hover: {
        background: 'disabled:hover:bg-transparent',
        border: 'disabled:hover:border disabled:hover:border-accent/5',
        text: 'disabled:hover:text-primary/20',
      },
    },
  },
};
export const buttonStyles = cva(
  `inline-flex items-center justify-center font-medium overflow-hidden ${roundednessConfig.roundedness}`,
  {
    variants: {
      intent: {
        primary: [
          spacingConfig.paddingWhenButtonIsNotGhostIntent,
          buttonColorClasses.primary.background,
          buttonColorClasses.primary.border,
          buttonColorClasses.primary.text,
          buttonColorClasses.primary.hover.background,
          buttonColorClasses.primary.hover.border,
          buttonColorClasses.primary.hover.text,
          buttonColorClasses.primary.active.background,
          buttonColorClasses.primary.active.border,
          buttonColorClasses.primary.disabled.background,
          buttonColorClasses.primary.disabled.border,
          buttonColorClasses.primary.disabled.text,
          buttonColorClasses.primary.disabled.hover.background,
          buttonColorClasses.primary.disabled.hover.border,
          buttonColorClasses.primary.disabled.hover.text,
        ],
        ghost: [
          spacingConfig.paddingWhenButtonIsGhostIntent,
          buttonColorClasses.ghost.background,
          buttonColorClasses.ghost.border,
          buttonColorClasses.ghost.text,
          buttonColorClasses.ghost.hover.text,
          buttonColorClasses.ghost.active.text,
          buttonColorClasses.ghost.disabled.text,
          buttonColorClasses.ghost.disabled.hover.text,
        ],
        secondary: [
          spacingConfig.paddingWhenButtonIsNotGhostIntent,
          buttonColorClasses.secondary.background,
          buttonColorClasses.secondary.border,
          buttonColorClasses.secondary.hover.background,
          buttonColorClasses.secondary.hover.border,
          buttonColorClasses.secondary.active.background,
          buttonColorClasses.secondary.active.border,
          buttonColorClasses.secondary.text,
          buttonColorClasses.secondary.disabled.background,
          buttonColorClasses.secondary.disabled.border,
          buttonColorClasses.secondary.disabled.text,
          buttonColorClasses.secondary.disabled.hover.background,
          buttonColorClasses.secondary.disabled.hover.border,
          buttonColorClasses.secondary.disabled.hover.text,
        ],
        ai: [
          spacingConfig.paddingWhenButtonIsNotGhostIntent,
          buttonColorClasses.ai.background,
          buttonColorClasses.ai.border,
          buttonColorClasses.ai.text,
          buttonColorClasses.ai.hover.background,
          buttonColorClasses.ai.hover.border,
          buttonColorClasses.ai.hover.text,
          buttonColorClasses.ai.active.background,
          buttonColorClasses.ai.active.border,
          buttonColorClasses.ai.active.text,
          buttonColorClasses.ai.disabled.background,
          buttonColorClasses.ai.disabled.border,
          buttonColorClasses.ai.disabled.text,
          buttonColorClasses.ai.disabled.hover.background,
          buttonColorClasses.ai.disabled.hover.border,
          buttonColorClasses.ai.disabled.hover.text,
        ],
        chip: [
          spacingConfig.paddingWhenButtonIsChipIntent,
          roundednessConfig.chipRoundedness,
          buttonColorClasses.chip.disabled.background,
          buttonColorClasses.chip.disabled.border,
          buttonColorClasses.chip.disabled.text,
          buttonColorClasses.chip.disabled.hover.background,
          buttonColorClasses.chip.disabled.hover.border,
          buttonColorClasses.chip.disabled.hover.text,
        ],
      },
      fullWidth: {
        true: sizingConfig.widthWhenButtonIsFullWidth,
      },
      alignLeft: {
        true: 'justify-start',
        false: 'justify-center',
      },
      clicked: {
        true: 'opacity-50',
        false: '',
      },
      flexHeight: {
        true: '',
        false: '',
      },
      active: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        intent: 'chip',
        active: true,
        class: `${buttonColorClasses.chip.active.background} 
                ${buttonColorClasses.chip.active.border} 
                ${buttonColorClasses.chip.active.text}`,
      },
      {
        intent: 'chip',
        active: false,
        class: `${buttonColorClasses.chip.background} 
                ${buttonColorClasses.chip.border} 
                ${buttonColorClasses.chip.text}
                ${buttonColorClasses.chip.hover.background}
                ${buttonColorClasses.chip.hover.border}
                ${buttonColorClasses.chip.hover.text}`,
      },
    ],
    defaultVariants: {
      intent: 'primary',
      fullWidth: false,
      alignLeft: false,
      clicked: false,
      flexHeight: false,
      active: false,
    },
  },
);
interface Props
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  children?: ReactNode;
  intent?: 'primary' | 'ghost' | 'secondary' | 'ai' | 'chip' | undefined | null;
  alignLeft?: boolean | undefined | null;
  flexHeight?: boolean | undefined | null;
  height?: string;
  disabled?: boolean;
  icon?: IconsName | ReactNode;
  loading?: boolean;
  iconStyles?: IconVariants;
  buttonId?: string;
  active?: boolean | null;
  chip?: {
    text: string;
    intent?: ChipIntent;
    className?: string;
  } | null;
}
const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      intent = 'primary',
      fullWidth,
      alignLeft = false,
      flexHeight = false,
      children,
      onClick,
      onFocus,
      icon,
      type = 'button',
      disabled = false,
      loading = false,
      className,
      iconStyles = { intent: 'accent', size: 'md' },
      buttonId,
      height = sizingConfig.height,
      active = false,
      chip = null,
    },
    ref,
  ) => {
    const [clicked, setClicked] = useState(false);
    const handleClick = (e: any) => {
      if (disabled || loading) return;
      setClicked(true);
      setTimeout(() => setClicked(false), 200);
      onClick?.(e);
    };
    return (
      <button
        id={buttonId}
        type={type}
        className={`${buttonStyles({
          intent,
          fullWidth,
          alignLeft,
          clicked,
          flexHeight,
          active,
          className,
        })} ${height} ${borderClassToVisualizeElementsAndSpacing}`}
        disabled={disabled || loading}
        onClick={handleClick}
        onFocus={onFocus}
        ref={ref}
      >
        <div
          className={`inline-flex items-center overflow-hidden 
            ${alignLeft ? 'justify-start' : 'justify-center'} 
            ${sizingConfig.innerWidth} 
            ${children ? spacingConfig.gap : ''} 
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {icon && !loading && (
            <div
              className={`flex items-center justify-center 
                ${roundednessConfig.iconRoundedness} 
                ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {typeof icon === 'string' ? (
                <Icon
                  name={icon as IconsName}
                  intent={disabled ? 'disabled' : iconStyles.intent}
                  size={iconStyles.size}
                  className={`${borderClassToVisualizeElementsAndSpacing}`}
                />
              ) : isValidElement(icon) ? (
                icon
              ) : null}
            </div>
          )}
          {children && (
            <div
              className={`flex items-center 
                ${alignLeft ? '' : 'justify-center'} 
                ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {loading && (
                <Loading
                  size="small"
                  fill={intent === 'ai' ? '#C691FF' : '#C4FAC3'}
                />
              )}
              {!loading && (
                <span
                  className={`text-body ${borderClassToVisualizeElementsAndSpacing}`}
                >
                  {children}
                </span>
              )}
            </div>
          )}
          {chip && !loading && (
            <Chip
              text={chip.text}
              intent={chip.intent || 'default'}
              className={chip.className || ''}
              setChipToNoMargin={true}
            />
          )}
        </div>
      </button>
    );
  },
);
Button.displayName = 'Button';
export default Button;
export type { Props as ButtonProps };
